import { useAdminAuth } from '@/store/admin/admin-auth-slice';

/**
 * Thin fetch wrapper for the admin app. Reads the JWT from the Zustand auth
 * slice and sends it on every request. On 401 it clears the session so the
 * ProtectedRoute guard redirects to /admin/login on next navigation.
 *
 * Deliberately not axios — the scheduling app uses raw fetch + Vite proxy
 * (`/api` → dev-api.scrips.com) and we mirror that for consistency.
 */

export class AdminApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(`Admin API Error ${status}: ${body}`);
    this.name = 'AdminApiError';
    this.status = status;
    this.body = body;
  }
}

const BASE_URL = (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

async function doFetch<T>(endpoint: string, init: RequestInit): Promise<T> {
  const token = useAdminAuth.getState().accessToken;
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.method && init.method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (res.status === 401) {
    useAdminAuth.getState().clear();
    throw new AdminApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    const body = await res.text();
    throw new AdminApiError(res.status, body);
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

/**
 * Per-call options accepted by every verb helper. Mirrors the canonical
 * `api-client.ts` `ApiCallOptions` shape — `signal` lets React Query
 * cancel the in-flight fetch when the consumer unmounts or the query
 * key changes. PROD-707.
 */
export interface AdminApiCallOptions {
  signal?: AbortSignal;
}

export const adminApi = {
  get: <T>(endpoint: string, opts: AdminApiCallOptions = {}): Promise<T> =>
    doFetch<T>(endpoint, { method: 'GET', signal: opts.signal }),
  post: <T>(endpoint: string, body?: unknown, opts: AdminApiCallOptions = {}): Promise<T> =>
    doFetch<T>(endpoint, {
      method: 'POST',
      ...(body != null ? { body: JSON.stringify(body) } : {}),
      signal: opts.signal,
    }),
  put: <T>(endpoint: string, body?: unknown, opts: AdminApiCallOptions = {}): Promise<T> =>
    doFetch<T>(endpoint, {
      method: 'PUT',
      ...(body != null ? { body: JSON.stringify(body) } : {}),
      signal: opts.signal,
    }),
  delete: <T>(endpoint: string, opts: AdminApiCallOptions = {}): Promise<T> =>
    doFetch<T>(endpoint, { method: 'DELETE', signal: opts.signal }),
};
